import type { Department } from "../contexts/AuthProvider";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import {
	DepartmentNames,
	DepartmentType,
	INHOUSE_ALF_DEPARTMENT_IDS,
	INHOUSE_SNF_DEPARTMENT_IDS,
	isIcuTeleDemoDepartment,
	isRealDemoDepartmentId,
} from "../utils/models";

export interface DepartmentFlags {
	timezone: string;
	isSnf: boolean;
	isAlf: boolean;
	isHospital: boolean;
	isAutoScheduler: boolean;
	isLowry: boolean;
	isColonial: boolean;
	isMedicalDepartment: boolean;
	isHospitalDemo: boolean;
	isSnfDemo: boolean;
	isAlfDemo: boolean;
	/** Live "real demo" dept (inhouse2__) that runs the actual product features on real data,
	 * unlike isAnyDemo which serves legacy fake/stub data. Mirrors backend is_real_demo_dept. */
	isRealDemo: boolean;
	/** Hospital visuals for a real hospital demo dept; unlike isHospitalDemo, no mock bypasses. */
	isHospitalView: boolean;
	/** The Holland ICU/Telemetry demo specifically — gates its per-area Shift-Insights breakdown. */
	isIcuTeleDemo: boolean;
	isAnyDemo: boolean;
}

/** What a department is (type, demo, timezone) — pure, so a page showing another department can ask about that one. */
export const getDepartmentFlags = (department: Department): DepartmentFlags => {
	const departmentId = department.department_id;
	const isHospital = department.type === DepartmentType.Hospital;
	const isRealDemo = isRealDemoDepartmentId(departmentId);
	const isHospitalDemo = departmentId === DepartmentNames.InHouseHospitalDemo;
	const isSnfDemo = departmentId === DepartmentNames.InHouseSNF;
	const isAlfDemo = departmentId === DepartmentNames.InHouseALF;
	// Folded into isAnyDemo so the hospital demo gets the same demo treatment as the SNF demo
	// (engagement badges, demo hiring, Management Center demo mode, etc.).
	const isHospitalView = isHospital && isRealDemo;

	return {
		// DEFAULT: the placeholder department rendered before the real one loads has no timezone.
		timezone: department.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
		isSnf: department.type === DepartmentType.SNF || INHOUSE_SNF_DEPARTMENT_IDS.includes(departmentId),
		isAlf: department.type === DepartmentType.ALF || INHOUSE_ALF_DEPARTMENT_IDS.includes(departmentId),
		isHospital,
		isAutoScheduler: departmentId === DepartmentNames.AutoScheduler,
		isLowry: departmentId === DepartmentNames.CenterAtLowryMainDept,
		isColonial: departmentId === DepartmentNames.ColonialMainDept,
		isMedicalDepartment: department.isSynergyMode,
		isHospitalDemo,
		isSnfDemo,
		isAlfDemo,
		isRealDemo,
		isHospitalView,
		// Matched on a name fragment, so both the original "Holland Hospital" seeds and the renamed
		// "ICU/Tele" one are covered, but not the other inhouse2 hospital demos.
		isIcuTeleDemo: isIcuTeleDemoDepartment(departmentId, isHospitalView),
		isAnyDemo: isHospitalDemo || isHospitalView || isSnfDemo || isAlfDemo,
	};
};

/** What the chosen department is (type, demo, timezone), derived from `useChosenDepartment` alone. */
export const useDepartmentFlags = (): DepartmentFlags => {
	const { chosenDepartment } = useChosenDepartment();
	return getDepartmentFlags(chosenDepartment);
};
